/**
 * @sazeo/sdk — People hooks
 *
 * TanStack Query wrappers for the People module (members, teams, team members).
 * All calls use the user JWT from packages/auth/. No service_role.
 *
 * Usage:
 *   const { data } = useTenantMembers(supabase, tenantId, { status: 'active' });
 */

import type {
  MembershipProfileUpdate,
  MembershipStatus,
  Team,
  TeamCreateInput,
  TeamMember,
  TeamMemberAddInput,
  TeamRole,
  TeamUpdateInput,
  UserProfileUpdate,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// SupabaseClientLike — minimal interface to avoid circular dep on packages/auth/
// ---------------------------------------------------------------------------

export interface SupabaseClientLike {
  auth: {
    getUser: () => Promise<{ data: { user: { id: string } | null } }>;
    // Reason: getSession is available on the concrete Supabase JS client used
    // at runtime; typed as unknown here to avoid pulling in the full type.
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
  from: (...args: any[]) => any;
  rpc?: (...args: any[]) => any;
}

// ---------------------------------------------------------------------------
// Pagination envelope
// ---------------------------------------------------------------------------

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  cursor: string | null;
}

// ---------------------------------------------------------------------------
// Member list options
// ---------------------------------------------------------------------------

export interface TenantMembersOptions {
  limit?: number;
  cursor?: string | null;
  search?: string;
  team_id?: string;
  status?: 'active' | 'invited' | 'suspended' | 'terminated';
}

export interface MemberDirectoryRow {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  email: string | null;
  title: string | null;
  department: string | null;
  status: MembershipStatus;
  last_seen: string | null;
  role_name: string | null;
  teams: Array<{ id: string; name: string }>;
}

export interface MemberDetail {
  user_id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  phone: string | null;
  timezone: string | null;
  title: string | null;
  department: string | null;
  status: MembershipStatus;
  bio: string | null;
  hired_at: string | null;
  employee_number: string | null;
  manager: { user_id: string; display_name: string | null } | null;
  teams: Array<{ id: string; name: string; role: TeamRole }>;
}

export interface TeamSummary extends Team {
  member_count: number;
  lead_display_name: string | null;
}

export interface TeamMemberListItem {
  user_id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  title: string | null;
  role: TeamRole;
  joined_at: string;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const peopleKeys = {
  all: ['people'] as const,
  me: () => [...peopleKeys.all, 'me'] as const,
  members: (tenantId: string, opts?: TenantMembersOptions) =>
    [...peopleKeys.all, 'members', tenantId, opts ?? {}] as const,
  member: (tenantId: string, userId: string) =>
    [...peopleKeys.all, 'member', tenantId, userId] as const,
  teams: (tenantId: string) => [...peopleKeys.all, 'teams', tenantId] as const,
  team: (tenantId: string, teamId: string) =>
    [...peopleKeys.all, 'team', tenantId, teamId] as const,
  teamMembers: (tenantId: string, teamId: string) =>
    [...peopleKeys.all, 'teamMembers', tenantId, teamId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal: extract Bearer token from Supabase session
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// User-level hooks
// ---------------------------------------------------------------------------

/** Own profile row from the `users` table. */
export function useMe(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: peopleKeys.me(),
    queryFn: () => apiFetch<Record<string, unknown>>(supabase, 'settings/me'),
  });
}

/** Update calling user's `users` row (user-level fields only). */
export function useUpdateProfile(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserProfileUpdate) =>
      apiFetch<Record<string, unknown>>(supabase, 'settings/me', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: peopleKeys.me() });
    },
  });
}

// ---------------------------------------------------------------------------
// Members hooks
// ---------------------------------------------------------------------------

/** Paginated list of tenant members (joined with memberships_profile). */
export function useTenantMembers(
  supabase: SupabaseClientLike,
  tenantId: string,
  opts: TenantMembersOptions = {},
) {
  return useQuery({
    queryKey: peopleKeys.members(tenantId, opts),
    queryFn: (): Promise<PaginatedResult<MemberDirectoryRow>> => {
      const params = new URLSearchParams({
        limit: String(opts.limit ?? 50),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.search ? { search: opts.search } : {}),
        ...(opts.team_id ? { team_id: opts.team_id } : {}),
        ...(opts.status ? { status: opts.status } : {}),
      });
      return apiFetch<PaginatedResult<MemberDirectoryRow>>(
        supabase,
        `people/members?${params.toString()}`,
      );
    },
    enabled: Boolean(tenantId),
  });
}

/** Single member profile (users + memberships_profile + teams). */
export function useMember(supabase: SupabaseClientLike, tenantId: string, userId: string) {
  return useQuery({
    queryKey: peopleKeys.member(tenantId, userId),
    queryFn: () => apiFetch<MemberDetail>(supabase, `people/members/${userId}`),
    enabled: Boolean(tenantId) && Boolean(userId),
  });
}

/** Update a membership_profile row. Admin/owner can update any; user can only update own. */
export function useUpdateMemberProfile(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: MembershipProfileUpdate }) =>
      apiFetch<Record<string, unknown>>(supabase, `people/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { userId }) => {
      void qc.invalidateQueries({ queryKey: peopleKeys.member(tenantId, userId) });
      void qc.invalidateQueries({ queryKey: peopleKeys.members(tenantId) });
    },
  });
}

/** Change a member's role. Admin can set member/manager/admin; owner can also set owner (transfer). */
export function useUpdateMembershipRole(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleName }: { userId: string; roleName: string }) =>
      apiFetch<{ user_id: string; role_name: string }>(supabase, `people/members/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role_name: roleName }),
      }),
    onSuccess: (_data, { userId }) => {
      void qc.invalidateQueries({ queryKey: peopleKeys.member(tenantId, userId) });
      void qc.invalidateQueries({ queryKey: peopleKeys.members(tenantId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Teams hooks
// ---------------------------------------------------------------------------

/** List all teams in the active tenant. */
export function useTeams(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: peopleKeys.teams(tenantId),
    queryFn: () => apiFetch<TeamSummary[]>(supabase, 'people/teams'),
    enabled: Boolean(tenantId),
  });
}

/** Single team detail. */
export function useTeam(supabase: SupabaseClientLike, tenantId: string, teamId: string) {
  return useQuery({
    queryKey: peopleKeys.team(tenantId, teamId),
    queryFn: () => apiFetch<Team>(supabase, `people/teams/${teamId}`),
    enabled: Boolean(tenantId) && Boolean(teamId),
  });
}

/** Create a team. Admin/owner only (enforced server-side). */
export function useCreateTeam(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TeamCreateInput) =>
      apiFetch<Team>(supabase, 'people/teams', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: peopleKeys.teams(tenantId) });
    },
  });
}

/** Update a team. Admin/owner only. */
export function useUpdateTeam(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, input }: { teamId: string; input: TeamUpdateInput }) =>
      apiFetch<Team>(supabase, `people/teams/${teamId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { teamId }) => {
      void qc.invalidateQueries({ queryKey: peopleKeys.teams(tenantId) });
      void qc.invalidateQueries({ queryKey: peopleKeys.team(tenantId, teamId) });
    },
  });
}

/** Delete a team. Admin/owner only. */
export function useDeleteTeam(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teamId: string) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(`/functions/v1/people/teams/${teamId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
    },
    onSuccess: (_data, teamId) => {
      void qc.invalidateQueries({ queryKey: peopleKeys.teams(tenantId) });
      void qc.invalidateQueries({ queryKey: peopleKeys.team(tenantId, teamId) });
    },
  });
}

/** List members of a team. */
export function useTeamMembers(supabase: SupabaseClientLike, tenantId: string, teamId: string) {
  return useQuery({
    queryKey: peopleKeys.teamMembers(tenantId, teamId),
    queryFn: () => apiFetch<TeamMemberListItem[]>(supabase, `people/teams/${teamId}/members`),
    enabled: Boolean(tenantId) && Boolean(teamId),
  });
}

/** Add a member to a team. Admin/owner only. */
export function useAddTeamMember(supabase: SupabaseClientLike, tenantId: string, teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TeamMemberAddInput) =>
      apiFetch<TeamMember>(supabase, `people/teams/${teamId}/members`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: peopleKeys.teamMembers(tenantId, teamId) });
    },
  });
}

/** Remove a member from a team. Admin/owner only. */
export function useRemoveTeamMember(
  supabase: SupabaseClientLike,
  tenantId: string,
  teamId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch(`/functions/v1/people/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: peopleKeys.teamMembers(tenantId, teamId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Presence
// ---------------------------------------------------------------------------

export interface OnlineUser {
  user_id: string;
  last_seen_at: string;
}

export const presenceKeys = {
  online: (tenantId: string) => ['presence', 'online', tenantId] as const,
} as const;

/**
 * Fetch users seen within the last 3 minutes for the active tenant.
 * Backed by the durable `last_seen_at` column (migration 0035).
 * For ephemeral real-time presence use `usePresence` from @sazeo/realtime.
 */
export function useOnlineUsers(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: presenceKeys.online(tenantId),
    queryFn: () =>
      apiFetch<{ data: OnlineUser[] }>(supabase, 'presence/online').then((res) => res.data),
    enabled: Boolean(tenantId),
    // Refresh every 60 s — aligns with heartbeat cadence.
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Org chart
// ---------------------------------------------------------------------------

export interface OrgChartMember {
  user_id: string;
  display_name: string | null;
  job_title: string | null;
  manager_id: string | null;
  avatar_url: string | null;
}

export function useOrgChart(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: ['org-chart', tenantId],
    queryFn: async () => {
      const { data, error } = await (
        supabase as unknown as {
          from: (table: string) => {
            select: (cols: string) => {
              eq: (
                col: string,
                val: string,
              ) => Promise<{ data: OrgChartMember[] | null; error: { message: string } | null }>;
            };
          };
        }
      )
        .from('memberships_profile')
        .select('user_id, display_name, job_title, manager_id, avatar_url')
        .eq('tenant_id', tenantId);
      if (error) throw new Error(error.message);
      return (data ?? []) as OrgChartMember[];
    },
    enabled: !!tenantId,
  });
}

// ---------------------------------------------------------------------------
// Membership role management
// ---------------------------------------------------------------------------

export interface MembershipRoleUpdate {
  user_id: string;
  role_name: string;
}

/** Update a membership's role (admin/owner only; enforced server-side). */
export function useUpdateMembership(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ user_id, role_name }: MembershipRoleUpdate) =>
      apiFetch<Record<string, unknown>>(supabase, `people/members/${user_id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role_name }),
      }),
    onSuccess: (_data, { user_id }) => {
      void qc.invalidateQueries({ queryKey: peopleKeys.members(tenantId) });
      void qc.invalidateQueries({ queryKey: peopleKeys.member(tenantId, user_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

export interface ImportJob {
  id: string;
  status: 'pending' | 'processing' | 'done' | 'failed';
  total_rows: number | null;
  processed_rows: number;
  error_count: number;
  errors: Array<{ row: number; message: string }>;
  created_at: string;
  updated_at: string;
}

export interface ImportResult {
  jobId: string;
  status: 'done' | 'failed';
  processed: number;
  errors: Array<{ row: number; message: string }>;
}

export const importKeys = {
  job: (jobId: string) => ['import', 'job', jobId] as const,
} as const;

/** Submit a CSV file for bulk employee import. Admin/owner only. */
export function useImportEmployees(supabase: SupabaseClientLike) {
  return useMutation({
    mutationFn: async (file: File): Promise<ImportResult> => {
      const headers = await getAuthHeaders(supabase);
      const body = new FormData();
      body.append('file', file);
      const res = await fetch('/functions/v1/people/import', {
        method: 'POST',
        headers,
        body,
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(err.error?.message ?? res.statusText);
      }
      return res.json() as Promise<ImportResult>;
    },
  });
}

/** Poll the status of a running import job. Refetches every 2 s while pending/processing. */
export function useImportJob(supabase: SupabaseClientLike, jobId: string | null) {
  return useQuery({
    queryKey: importKeys.job(jobId ?? ''),
    queryFn: () => apiFetch<ImportJob>(supabase, `people/import/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'processing' ? 2000 : false;
    },
  });
}

// ---------------------------------------------------------------------------
// RPC-based CSV import (B40) — calls import_employees_transaction directly
// ---------------------------------------------------------------------------

export interface RPCImportResult {
  imported: number;
  errors: number;
  skipped: number;
  created_ids: string[];
}

/**
 * Calls `import_employees_transaction` Postgres RPC via supabase.rpc().
 * Used by ImportEmployeesModal (multi-step advanced import, B40).
 * Invalidates people/members query on success.
 */
export function useImportEmployeesRPC(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      rows,
      conflictMode,
    }: {
      rows: unknown[];
      conflictMode: 'abort' | 'skip';
    }): Promise<RPCImportResult> => {
      const { data: userData } = await supabase.auth.getUser();
      const actorUserId = userData?.user?.id ?? '';
      const result = await (supabase.rpc as NonNullable<SupabaseClientLike['rpc']>)(
        'import_employees_transaction',
        {
          p_tenant_id: tenantId,
          p_rows: rows,
          p_conflict_mode: conflictMode,
          p_actor_user_id: actorUserId,
        },
      );
      if ((result as { error?: { message?: string } }).error) {
        const err = (result as { error: { message?: string } }).error;
        throw new Error(err.message ?? 'Import failed');
      }
      const data = (result as { data: RPCImportResult }).data;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: peopleKeys.members(tenantId) });
    },
  });
}

// ---------------------------------------------------------------------------
// W3-A canonical aliases — spec-mandated hook names
// ---------------------------------------------------------------------------
export const useCurrentUser = useMe;
export const useMemberships = useTenantMembers;
export const useMembershipProfile = useMember;
